import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { INSURANCE_CONVENTION_COMMISSIONS_QUERY, INSURANCE_TYPES_QUERY } from '../../../queries';
import { ADD_UPDATE_CONVENTION_COMMISSIONS, DELETE_INSURANCE_CONVENTION_COMMISSION } from '../../../mutations';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import { Grid, Box, Icon, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import { sortBy } from 'utils/functions';
import { INSURANCE_CONVENTION_COMMISSION_DELETED, INSURANCE_CONVENTION_COMMISSION_UPDATED } from '../../../subscriptions';
import { StyledHeaderCell, StyledGrayIcon, StyledTable } from 'components/common/Styles';
import InsuranceConventionCommissionListItem from './InsuranceConventionCommissionListItem';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { conventionCurrencyCodeVar } from '../../../variables';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const InsuranceConventionCommissionContainer = ({ productId }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const [commissions, setCommissions] = useState([]);
  const [inAdd, setInAdd] = useState(false);
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const currencyCode = useReactiveVar(conventionCurrencyCodeVar);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const { loading } = useQuery(INSURANCE_CONVENTION_COMMISSIONS_QUERY, {
    variables: { productId },
    skip: !productId,
    onCompleted: data => {
      const newCommissions = (data?.insuranceConventionCommissions || []).map(x => ({
        ...x,
        disabled: false,
        editing: false,
        isNew: false
      }));
      setCommissions(newCommissions);
    }
  });

  const { data: insuranceTypesData } = useQuery(INSURANCE_TYPES_QUERY);

  const insuranceTypes = useMemo(() => insuranceTypesData?.insuranceTypes || [], [insuranceTypesData]);

  const updateLocalCommission = useCallback(
    (insuranceTypeId, property, value) => {
      setCommissions(prev => {
        const modifyCommission = commission => {
          const newCommission = { ...commission };
          newCommission[property] = value;
          if (property === 'editing' && !value) newCommission.disabled = false;
          return newCommission;
        };
        const newCommissions = prev.map(x =>
          x.productId === productId && x.insuranceTypeId === insuranceTypeId ? modifyCommission(x) : x
        );
        return newCommissions;
      });
    },
    [productId]
  );

  const [addOrUpdateConventionCommission] = useMutation(ADD_UPDATE_CONVENTION_COMMISSIONS, {
    onError: ({ params }) => {
      updateLocalCommission(params.insuranceTypeId, 'disabled', false);
    }
  });

  const [deleteInsuranceConventionCommission] = useMutation(DELETE_INSURANCE_CONVENTION_COMMISSION, {
    onError: ({ params }) => {
      updateLocalCommission(params.insuranceTypeId, 'disabled', false);
    }
  });

  const handleAdd = useCallback(() => {
    setCommissions(prev => [{ isNew: true, key: crypto.randomUUID(), productId }, ...prev]);
    setInAdd(true);
  }, [productId]);

  const handleCancelAdd = useCallback(() => {
    setCommissions(prev => prev.filter((_, i) => i !== 0));
    setInAdd(false);
  }, []);

  const handleAddOrUpdateCommission = useCallback(
    commission => {
      if (inAdd) {
        if (commissions.some(x => x.productId === commission.productId && x.insuranceTypeId === commission.insuranceTypeId)) {
          addToast(t('InsuranceConventions.Details.Commissions.CommissionExists'), 'error');
          return;
        }
      }
      if (
        commissions.some(
          x =>
            x.productId === commission.productId &&
            x.insuranceTypeId === commission.insuranceTypeId &&
            x.commission === commission.commission
        )
      ) {
        addToast(t('InsuranceConventions.Details.Commissions.CommissionExists'), 'error');
        return;
      }
      updateLocalCommission(commission.initialInsuranceTypeId, 'disabled', true);

      // eslint-disable-next-line no-unused-vars
      const { isNew, key, editing, disabled, insuranceTypeName, ...sanitizedCommission } = commission;

      addOrUpdateConventionCommission({
        variables: { input: sanitizedCommission },
        errorHandlingParams: { insuranceTypeId: commission.initialInsuranceTypeId }
      });
    },
    [commissions, updateLocalCommission, addOrUpdateConventionCommission, addToast, t, inAdd]
  );

  const handleDeleteCommission = useCallback(
    (productId, insuranceTypeId) => {
      confirm(
        () => {
          updateLocalCommission(insuranceTypeId, 'disabled', true);
          deleteInsuranceConventionCommission({ variables: { productId, insuranceTypeId }, errorHandlingParams: { insuranceTypeId } });
        },
        {
          title: t('InsuranceConventions.Details.Commissions.DeleteCommission'),
          content: t('InsuranceConventions.Details.Commissions.AreYouSureDeleteCommission')
        }
      );
    },
    [confirm, deleteInsuranceConventionCommission, t, updateLocalCommission]
  );

  useSubscription(INSURANCE_CONVENTION_COMMISSION_UPDATED, {
    onData: ({
      data: {
        data: {
          insuranceConventionCommissionUpdated: { isNew, productId, initialInsuranceTypeId, commission }
        }
      }
    }) => {
      if (isNew) {
        handleCancelAdd();
        setCommissions(prev => {
          const newCommissions = [...prev, commission];
          sortBy(newCommissions, ['productId', 'insuranceTypeId']);
          return newCommissions;
        });
      } else {
        setCommissions(prev => {
          const newCommissions = prev.map(x =>
            x.productId === productId && x.insuranceTypeId === initialInsuranceTypeId
              ? { ...commission, editing: false, disabled: false }
              : x
          );
          sortBy(newCommissions, ['productId', 'insuranceTypeId']);
          return newCommissions;
        });
      }
      addCachedDetail(
        client,
        { name: 'insuranceConventionCommissions', value: INSURANCE_CONVENTION_COMMISSIONS_QUERY },
        { productId },
        x => x.productId === commission.productId && x.insuranceTypeId === initialInsuranceTypeId,
        commission,
        ['productId', 'insuranceTypeId']
      );
      addToast(t('InsuranceConventions.Details.Commissions.CommissionSaved'), 'success');
    }
  });

  useSubscription(INSURANCE_CONVENTION_COMMISSION_DELETED, {
    onData: ({
      data: {
        data: {
          insuranceConventionCommissionDeleted: { productId, insuranceTypeId }
        }
      }
    }) => {
      setCommissions(prev => prev.filter(x => !(x.productId === productId && x.insuranceTypeId === insuranceTypeId)));
      removeCachedDetail(
        client,
        { name: 'insuranceConventionCommissions', value: INSURANCE_CONVENTION_COMMISSIONS_QUERY },
        { productId },
        x => x.productId === productId && x.insuranceTypeId === insuranceTypeId,
        ['productId', 'insuranceTypeId']
      );
      addToast(t('InsuranceConventions.Details.Commissions.CommissionDeleted'), 'success');
    }
  });

  const getCommissionKey = useCallback(
    commission => (commission.isNew ? commission.key : `${commission.productId}_${commission.insuranceTypeId}`),
    []
  );

  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>{t('InsuranceConventions.InsuranceType')}</StyledHeaderCell>
              <StyledHeaderCell align='left'>
                {t('InsuranceConventions.Details.Commissions.Header', { currency: currencyCode })}
              </StyledHeaderCell>
              <StyledHeaderCell align='right'>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={inAdd || isNewConvention || !canWrite}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Add')}
                </Button>
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && loading && (
                <TableCell colSpan={3}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!isNewConvention && !loading && commissions.length === 0 && (
                <TableCell colSpan={3}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loading &&
              commissions.length > 0 &&
              commissions.map(commission => (
                <InsuranceConventionCommissionListItem
                  key={getCommissionKey(commission)}
                  commission={commission}
                  insuranceTypes={insuranceTypes}
                  onAddOrUpdateCommission={handleAddOrUpdateCommission}
                  onUpdateLocalCommission={updateLocalCommission}
                  onDelete={handleDeleteCommission}
                  onCancelAdd={handleCancelAdd}
                />
              ))}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

InsuranceConventionCommissionContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionCommissionContainer;
